import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Button,
  Paper,
  Chip,
  Divider,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Link as LinkIcon,
  Email as EmailIcon,
  Code as EmbedIcon,
  QrCode as QRIcon,
  Share as ShareIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import share from '../../assets/images/share.webp';

const ShareOptions = () => {
  const shareOptions = [
    {
      id: 'direct-link',
      title: 'Direct Link',
      icon: <LinkIcon color="primary" />,
      description: 'Share a direct URL that anyone can access to take your survey.',
      useCases: ['Social media sharing', 'Messaging apps', 'General distribution', 'Quick sharing'],
      pros: ['Instant sharing', 'Works everywhere', 'No setup required', 'Mobile-friendly'],
      cons: ['No respondent tracking', 'Public access', 'No personalization'],
      example: 'https://surveyslack.com/form/abc123',
      bestFor: 'Quick, wide distribution to anonymous respondents'
    },
    {
      id: 'email-invites',
      title: 'Email Invitations',
      icon: <EmailIcon color="primary" />,
      description: 'Send personalized email invitations to specific recipients with tracking.',
      useCases: ['Customer surveys', 'Employee feedback', 'Targeted research', 'Follow-up surveys'],
      pros: ['Personalized messages', 'Response tracking', 'Reminder emails', 'Professional appearance'],
      cons: ['Requires email list', 'May go to spam', 'Setup time required'],
      example: 'Personalized email with survey link and tracking',
      bestFor: 'Professional surveys with known recipients'
    },
    {
      id: 'embed-code',
      title: 'Embed Code',
      icon: <EmbedIcon color="primary" />,
      description: 'Embed your survey directly into your website, blog, or application.',
      useCases: ['Website integration', 'Blog posts', 'Customer portals', 'App integration'],
      pros: ['Seamless integration', 'Matches your branding', 'Higher completion rates', 'No redirect needed'],
      cons: ['Technical setup required', 'Limited customization', 'Responsive design considerations'],
      example: '<iframe src="https://surveyslack.com/embed/abc123" width="100%" height="600"></iframe>',
      bestFor: 'Website owners wanting seamless user experience'
    },
    {
      id: 'qr-code',
      title: 'QR Code',
      icon: <QRIcon color="primary" />,
      description: 'Generate a QR code that users can scan with their mobile devices.',
      useCases: ['Print materials', 'Events', 'Physical locations', 'Mobile-first surveys'],
      pros: ['Mobile-optimized', 'Works offline to online', 'Easy scanning', 'Print-friendly'],
      cons: ['Requires QR scanner', 'Limited to mobile users', 'No tracking by default'],
      example: 'Downloadable QR code image for print or digital use',
      bestFor: 'Events, print materials, and mobile-first distribution'
    }
  ];

  const securityFeatures = [
    'Password protection for sensitive surveys',
    'IP address restrictions for internal surveys',
    'Response limits to prevent spam',
    'Anonymous vs. tracked response options',
    'SSL encryption for all survey data',
    'GDPR-compliant data handling'
  ];

  const trackingFeatures = [
    'Real-time response monitoring',
    'Completion rate tracking',
    'Source attribution (where responses came from)',
    'Time-based analytics',
    'Geographic response data',
    'Device and browser information'
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700,  }}>
        Share Options
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4,  }}>
        Learn about all the ways you can share your surveys and reach your target audience effectively.
      </Typography>

      {/* Screenshot (Share modal) */}
      <Box sx={{ mb: 4 }}>
        <Box
          component="img"
          src={share}
          alt="Share Survey modal showing link, social icons and QR code"
          sx={{
            width: '100%',
            borderRadius: 1,
            display: 'block',
            boxShadow: (theme) => theme.shadows[3],
            // border: '1px solid',
            // borderColor: 'divider'
          }}
        />
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          Share modal: direct link field, social icons, and downloadable QR code
        </Typography>
      </Box>

      {/* Overview */}
      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="body2">
          <strong>Choose the right sharing method</strong> based on your audience, goals, and technical requirements. 
          You can use multiple sharing methods for the same survey.
        </Typography>
      </Alert>

      {/* Share Options Grid */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Available Sharing Methods
        </Typography>
        <Grid container spacing={3}>
          {shareOptions.map((option) => (
            <Grid item xs={12} key={option.id}>
              <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    {option.icon}
                    <Typography variant="h5" sx={{ ml: 1, fontWeight: 500 }}>
                      {option.title}
                    </Typography>
                    <Chip 
                      label={`Best for: ${option.bestFor}`} 
                      color="primary" 
                      variant="outlined" 
                      sx={{ ml: 'auto' }}
                    />
                  </Box>
                  
                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                    {option.description}
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                        Common Use Cases
                      </Typography>
                      <List dense>
                        {option.useCases.map((useCase, index) => (
                          <ListItem key={index} sx={{ py: 0.5, pl: 0 }}>
                            <ListItemIcon sx={{ minWidth: 30 }}>
                              <CheckIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText primary={useCase} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: 'success.main' }}>
                        Advantages
                      </Typography>
                      <List dense>
                        {option.pros.map((pro, index) => (
                          <ListItem key={index} sx={{ py: 0.5, pl: 0 }}>
                            <ListItemIcon sx={{ minWidth: 30 }}>
                              <CheckIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText primary={pro} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: 'warning.main' }}>
                        Considerations
                      </Typography>
                      <List dense>
                        {option.cons.map((con, index) => (
                          <ListItem key={index} sx={{ py: 0.5, pl: 0 }}>
                            <ListItemIcon sx={{ minWidth: 30 }}>
                              <InfoIcon fontSize="small" color="warning" />
                            </ListItemIcon>
                            <ListItemText primary={con} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                    Example
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: 'grey.50', fontFamily: 'monospace' }}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {option.example}
                    </Typography>
                  </Paper>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Security & Privacy */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Security & Privacy Features
        </Typography>
        <Card sx={{ background: 'linear-gradient(135deg, #f6f9fc 0%, #f1f4f8 100%)' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <SecurityIcon color="primary" />
              <Typography variant="h5" sx={{ ml: 1, fontWeight: 500 }}>
                Keep Your Surveys Secure
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              SurveySlack provides multiple security options to protect your surveys and respondent data:
            </Typography>
            <List>
              {securityFeatures.map((feature, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>

      {/* Tracking & Analytics */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Response Tracking & Analytics
        </Typography>
        <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <AnalyticsIcon color="primary" />
              <Typography variant="h5" sx={{ ml: 1, fontWeight: 500 }}>
                Track Your Survey Performance
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              Monitor how your surveys are performing with detailed analytics:
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <List>
                  {trackingFeatures.slice(0, 3).map((feature, index) => (
                    <ListItem key={index} sx={{ py: 0.5, pl: 0 }}>
                      <ListItemIcon>
                        <CheckIcon color="success" />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <List>
                  {trackingFeatures.slice(3).map((feature, index) => (
                    <ListItem key={index} sx={{ py: 0.5, pl: 0 }}>
                      <ListItemIcon>
                        <CheckIcon color="success" />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Best Practices */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Sharing Best Practices
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Alert severity="success">
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                Maximize Response Rates
              </Typography>
              <List dense>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Use clear, compelling subject lines for emails" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Explain the purpose and time commitment" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Send reminder emails for important surveys" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Choose the right time to send invitations" />
                </ListItem>
              </List>
            </Alert>
          </Grid>
          <Grid item xs={12} md={6}>
            <Alert severity="info">
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                Technical Considerations
              </Typography>
              <List dense>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Test all sharing methods before launch" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Ensure mobile compatibility" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Monitor for spam filters with email invites" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="Use UTM parameters for tracking" />
                </ListItem>
              </List>
            </Alert>
          </Grid>
        </Grid>
      </Box>

      {/* Quick Setup Guide */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Quick Setup Guide
        </Typography>
        <Card sx={{ border: '1px solid', borderColor: 'primary.light' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
              How to Share Your Survey
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <Typography variant="h6" color="primary">1</Typography>
                </ListItemIcon>
                <ListItemText 
                  primary="Publish your survey" 
                  secondary="Make sure your survey is complete and published"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Typography variant="h6" color="primary">2</Typography>
                </ListItemIcon>
                <ListItemText 
                  primary="Choose sharing method" 
                  secondary="Select the most appropriate method for your audience"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Typography variant="h6" color="primary">3</Typography>
                </ListItemIcon>
                <ListItemText 
                  primary="Configure settings" 
                  secondary="Set up security, tracking, and other options as needed"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Typography variant="h6" color="primary">4</Typography>
                </ListItemIcon>
                <ListItemText 
                  primary="Distribute and monitor" 
                  secondary="Share your survey and track responses in real-time"
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Box>

      {/* Next Steps */}
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
          Ready to share your survey?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Learn how to analyze the responses you collect.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" href="/app/documentations/analytics/response-analytics">
            Response Analytics
          </Button>
          <Button variant="outlined" href="/app/dashboard">
            Start Sharing
          </Button>
        </Box>
      </Box>

        {/* Bottom navigation CTA (Previous / Next) */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Button  color="inherit"  variant="contained" href="/app/documentations/building-surveys/question-types">
                      Previous
                    </Button>
                    <Button variant="contained"  href="/app/documentations/analyzing-results/viewing-analytics">
                      Next
                    </Button>
                  </Box>
    </Box>
  );
};

export default ShareOptions;